import type {
  ArtifactCreationBody,
  ArtifactSimpleResource,
  InputCustomModel,
  InputModel,
  Translations,
} from '@ingenico/react-form-builder';
import { InputTypeEnum, Z_INDEX_PARAMETER_MODULE_DIALOG } from '@ingenico/react-form-builder';
import type { MappedSmartFieldResourceApi } from '@ingenico/react-smartfield-utils';
import { Banner } from '@ingenico/react-ui/banner';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreDialog } from '@ingenico/react-ui/core-dialog';
import { CoreIconButton } from '@ingenico/react-ui/core-icon-button';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { Checkbox, DialogActions, DialogContent, DialogTitle, LinearProgress } from '@mui/material';
import { toString } from 'lodash-es';
import type { ReactElement } from 'react';
import type { SmartFieldCategoryOption, SmartFieldsResource } from '../../../domain/models/smartFields.model';

import { ParameterModuleSmartFieldCreateContent } from '../parameter-module-smart-field-content/ParameterModuleSmartFieldCreateContent.ctrl';
import { ParameterModuleSmartFieldSearchContent } from '../parameter-module-smart-field-content/ParameterModuleSmartFieldSearchContent.ctrl';
import styles from './ParameterModuleMapSmartFieldsModal.module.scss';
import { useModuleDetailsMapSmartFieldsModal } from './useModuleDetailsMapSmartFieldsModal.vm';

export interface ParameterModuleMapSmartFieldsModalProps {
  readonly isOpen: boolean;
  readonly onClose?: () => void;
  readonly onConfirm?: (newSmartField?: SmartFieldsResource) => void;
  readonly onConfirmCreation: (inputKey: string, newSmartField: MappedSmartFieldResourceApi) => void;
  readonly mode?: 'edit' | 'search';
  readonly isLoading?: boolean;
  readonly hasError?: boolean;
  readonly options?: SmartFieldsResource[];
  readonly selectedSmartField?: SmartFieldsResource;
  readonly handleChangeCategories: (newCategories: SmartFieldCategoryOption[]) => void;
  readonly categoryOptions: SmartFieldCategoryOption[];
  readonly isLoadingCategories: boolean;
  readonly selectedCategories: SmartFieldCategoryOption[];
  readonly parameter?: InputModel<InputCustomModel>;
  readonly translations: Translations;
  readonly inputKey?: string;
  readonly handleCategorySearchChange: (search: string) => void;
  readonly handleSmartfieldSearchChange: (search: string) => void;
  readonly artifacts: ArtifactSimpleResource[];
  readonly artifactsIsLoading: boolean;
  readonly onArtifactSearch: (query: string, acceptedExtensions?: readonly string[]) => void;
  readonly onArtifactCreate: (
    payload: ArtifactCreationBody,
    onSuccess?: (artifact: ArtifactSimpleResource) => void,
    onError?: () => void,
  ) => void;
  readonly onArtifactPreview: (id: string) => void;

  readonly onSubmit?: (value: SmartFieldsResource) => void;
}

export function ParameterModuleMapSmartFieldsModal({
  isOpen,
  onClose,
  onConfirm,
  onConfirmCreation,
  mode,
  isLoading,
  hasError,
  options = [],
  selectedSmartField,
  categoryOptions,
  isLoadingCategories,
  parameter,
  translations,
  inputKey,
  handleChangeCategories,
  selectedCategories,
  handleCategorySearchChange,
  handleSmartfieldSearchChange,
  artifacts,
  artifactsIsLoading,
  onArtifactSearch,
  onArtifactCreate,
  onArtifactPreview,
}: ParameterModuleMapSmartFieldsModalProps): ReactElement {
  const {
    title,
    cancelButtonLabel,
    confirmButtonLabel,
    handleConfirm,
    handleRequestClose,
    handleExited,
    handleToggleCreation,
    isCreation,
    customPaperClassName,
    isStructureWide,
    searchContent,
    categories,
    toggleCreationLabel,
    creation,
    handleFormValidation,
    isCategoryFeatureEnabled,
    onFormChange,
    isFormValid,
    handleSmartfieldSearchChange: handleSmartfieldSearchChangeModal,
  } = useModuleDetailsMapSmartFieldsModal({
    mode,
    onConfirm,
    onConfirmCreation,
    onClose,
    selectedSmartField,
    hasError,
    categoryOptions,
    handleChangeCategories,
    isLoadingCategories,
    selectedCategories,
    inputKey: toString(inputKey),
    handleCategorySearchChange,
    parameterDataType: parameter?.inputConfiguration.type === InputTypeEnum.Table ? 'STRUCTURE' : undefined,
    isOpen,
    handleSmartfieldSearchChange,
  });

  return (
    <CoreDialog
      onClose={handleRequestClose}
      classes={{
        paper: customPaperClassName,
        root: styles.basicDialogModal,
      }}
      fullWidth
      maxWidth={isStructureWide ? false : 'sm'}
      open={isOpen}
      slotProps={{ transition: { onExited: handleExited } }}
      sx={{ zIndex: Z_INDEX_PARAMETER_MODULE_DIALOG }}
      disableEnforceFocus
    >
      <DialogTitle className={styles.header}>
        <div className={styles.dialogTitle}>
          <CoreTypography variant={CoreTypographyVariant.Title01Medium}>{title}</CoreTypography>
        </div>
        <CoreIconButton onClick={handleRequestClose}>
          <CloseIcon />
        </CoreIconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {isLoading && !hasError && <LinearProgress />}
        {!isLoading && hasError && <Banner message={searchContent.errorMessage} />}
        {!isLoading && !hasError && (
          <>
            <ParameterModuleSmartFieldSearchContent
              isLoading={isLoading}
              componentLabel={searchContent.componentLabel}
              handleChange={searchContent.handleChange}
              hasError={hasError}
              options={options}
              placeholder={searchContent.placeholder}
              selected={searchContent.selected}
              categories={categories}
              isCreation={isCreation}
              isCategoryFeatureEnabled={isCategoryFeatureEnabled}
              handleSmartfieldSearchChange={handleSmartfieldSearchChangeModal}
            />
            <div className={styles.checkbox}>
              <Checkbox checked={isCreation} value={isCreation} onChange={() => handleToggleCreation()} />
              <CoreTypography variant={CoreTypographyVariant.Body02Regular}>{toggleCreationLabel}</CoreTypography>
            </div>
            {isCreation && parameter && (
              <ParameterModuleSmartFieldCreateContent
                translations={translations}
                parameter={parameter}
                categories={creation}
                handleFormValidation={handleFormValidation}
                isCategoryFeatureEnabled={isCategoryFeatureEnabled}
                onFormChange={onFormChange}
                artifacts={artifacts}
                artifactsIsLoading={artifactsIsLoading}
                onArtifactSearch={onArtifactSearch}
                onArtifactCreate={onArtifactCreate}
                onArtifactPreview={onArtifactPreview}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        <div className={styles.action}>
          <CoreButton size="small" onClick={handleRequestClose} variant={'text'} disabled={isLoading}>
            {cancelButtonLabel}
          </CoreButton>
          <CoreButton
            startIcon={hasError ? <ReplayIcon /> : undefined}
            onClick={handleConfirm}
            loading={isLoading}
            variant="contained"
            size="small"
            disabled={(!isCreation && !searchContent.selected) || (isCreation && !isFormValid)}
          >
            {confirmButtonLabel}
          </CoreButton>
        </div>
      </DialogActions>
    </CoreDialog>
  );
}
